import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAnalyticsQueries, useSessionQuery } from "@/lib/queries";
import type { AnalyticsDay, DateRange, DocumentUsage, UserUsage } from "@/types";
import { DateRangePicker, defaultRange } from "@/components/analytics/DateRangePicker";
import { StatTile, PlainTile } from "@/components/charts/StatTile";
import { TimeSeries } from "@/components/charts/TimeSeries";
import { AdminOnly } from "@/components/layout/AdminOnly";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  SortableHead,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useSort,
} from "@/components/ui/table";
import { Page, PageHeader, PageScroll } from "@/components/layout/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { datedFilename, downloadCsv } from "@/lib/csv";

export const Route = createFileRoute("/admin/analytics")({
  component: AnalyticsPage,
});

const dateFmt = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });

function AnalyticsPage() {
  const session = useSessionQuery();
  const [range, setRange] = useState<DateRange>(defaultRange);

  return (
    <AdminOnly role={session.data?.user.role}>
      <Page>
        <PageHeader title="Analytics" description="Usage, reach and cost for this organisation." />
        {/*
          A dashboard is the one page shape where the page itself is the right
          scroll region: the cards are of unknown and unequal height, and
          capping each of them — as three of these were, at 420px — means a
          page that scrolls containing three boxes that also scroll, none of
          which can show you its own bottom row. The caps are gone and the
          tables are as long as their data.
        */}
        <PageScroll>
          <AnalyticsBody range={range} onRangeChange={setRange} />
        </PageScroll>
      </Page>
    </AdminOnly>
  );
}

function AnalyticsBody({
  range,
  onRangeChange,
}: {
  range: DateRange;
  onRangeChange: (r: DateRange) => void;
}) {
  const { overview, activity, documents, users } = useAnalyticsQueries(range);

  const series = useMemo(() => {
    const days = activity.data?.days ?? [];
    return {
      questions: days.map((d) => ({ date: d.date, value: d.questions })),
      users: days.map((d) => ({ date: d.date, value: d.users })),
      documents: days.map((d) => ({ date: d.date, value: d.documents })),
    };
  }, [activity.data]);

  const o = overview.data;

  return (
    <>
      <div className="mb-4">
        <DateRangePicker value={range} onChange={onRangeChange} />
      </div>

      {overview.isError && (
        <Card className="mb-4 p-5">
          <p className="text-sm text-danger-text">These reports could not be loaded.</p>
        </Card>
      )}

      {/* The headline numbers render as soon as the cheap query lands; the
          tables below fill in behind them rather than holding the page. */}
      <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {o ? (
          <>
            <StatTile
              label="Questions asked"
              metric={o.questions}
              caption={`${o.answered} answered · ${o.failed} failed`}
            />
            <StatTile label="Active people" metric={o.activeUsers} />
            <StatTile
              label="Documents cited"
              metric={o.documents}
              caption="Distinct documents appearing in an answer"
            />
            <StatTile
              label="Pages cited"
              metric={o.pages}
              caption="Distinct pages appearing in an answer"
            />
          </>
        ) : (
          Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-[104px] animate-pulse rounded-card border border-border bg-card" />
          ))
        )}
      </div>

      {o && (
        <div className="mb-3 grid gap-3 sm:grid-cols-3">
          <PlainTile
            label="Average rating"
            value={o.avgRating !== null ? o.avgRating.toFixed(2) : "—"}
            caption={
              o.ratedCount > 0
                ? `From ${o.ratedCount.toLocaleString()} rating${o.ratedCount === 1 ? "" : "s"}`
                : "Nothing in this period has been rated"
            }
          />
          <PlainTile
            label="Input tokens"
            value={o.inputTokens.toLocaleString()}
            caption="Billed to this organisation's gateway client"
          />
          <PlainTile
            label="Output tokens"
            value={o.outputTokens.toLocaleString()}
            caption="Billed to this organisation's gateway client"
          />
        </div>
      )}

      {/* Three measures, three axes. Putting questions, people and documents on
          one chart would need a second y-scale, and the point where two such
          lines cross means nothing at all. */}
      <div className="mb-3 grid gap-3 lg:grid-cols-3">
        <TimeSeries points={series.questions} label="Questions per day" />
        <TimeSeries points={series.users} label="Active people per day" />
        <TimeSeries points={series.documents} label="Documents cited per day" />
      </div>

      <div className="grid items-start gap-3 lg:grid-cols-2">
        <DocumentUsageTable range={range} rows={documents.data?.documents ?? []} loading={documents.isLoading} />
        <UserUsageTable rows={users.data?.users ?? []} loading={users.isLoading} />
      </div>

      {/* ERIKA's API Usage section.
          The daily rows have always come back from GET /api/analytics/activity
          and were only ever charted. A chart answers "is it going up"; a table
          answers "what happened on the 14th", which is the question somebody
          asks when a number looks wrong. */}
      <ApiUsage range={range} days={activity.data?.days ?? []} loading={activity.isLoading} />
    </>
  );
}

/**
 * Total days, total API calls, average daily calls, and the day-by-day table.
 *
 * "API calls" is `sum(documents referenced)`, which is what ERIKA meant by it
 * and is not the number of HTTP requests anybody made. Saying so on the tile
 * matters: an administrator comparing this against an invoice needs to know
 * which quantity they are looking at.
 */
function ApiUsage({
  range,
  days,
  loading,
}: {
  range: DateRange;
  days: AnalyticsDay[];
  loading: boolean;
}) {
  const [filter, setFilter] = useState("");
  const { sort, dir, headProps } = useSort<keyof AnalyticsDay>("date", "desc", {
    users: "desc",
    questions: "desc",
    documents: "desc",
  });

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const filtered = q ? days.filter((d) => d.date.toLowerCase().includes(q)) : days;
    return [...filtered].sort((a, b) => {
      if (sort.key === "date") return (Date.parse(a.date) - Date.parse(b.date)) * dir;
      return ((a[sort.key] as number) - (b[sort.key] as number)) * dir;
    });
  }, [days, filter, sort, dir]);

  const totals = useMemo(() => {
    const calls = days.reduce((a, d) => a + d.documents, 0);
    return {
      // Days with activity, not days in the window. "Fourteen days of use"
      // and "a fortnight elapsed" are different claims and only the first is
      // one this data can make.
      days: days.length,
      calls,
      average: days.length > 0 ? calls / days.length : 0,
    };
  }, [days]);

  return (
    <div className="mt-3">
      <div className="mb-3 grid gap-3 sm:grid-cols-3">
        <PlainTile
          label="Total days"
          value={totals.days.toLocaleString()}
          caption="Days in this window with any recorded activity"
        />
        <PlainTile
          label="Total API calls"
          value={totals.calls.toLocaleString()}
          caption="Documents referenced across every answer"
        />
        <PlainTile
          label="Average daily calls"
          value={totals.days > 0 ? totals.average.toFixed(1) : "—"}
          caption="Total calls ÷ days with activity"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daily activity</CardTitle>
          <div className="flex items-center gap-2">
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by date"
              className="h-8 w-36 text-xs"
            />
            <Button
              size="sm"
              variant="secondary"
              className="text-xs"
              disabled={rows.length === 0}
              onClick={() =>
                downloadCsv(
                  // The window, not today's date: two exports of the same
                  // report over different periods must not collide, and the
                  // period is what tells them apart.
                  `erika-activity-${range.start}-to-${range.end}.csv`,
                  ["date", "questions", "active_people", "documents_referenced"],
                  rows.map((d) => [d.date.slice(0, 10), d.questions, d.users, d.documents]),
                )
              }
            >
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
          {!loading && rows.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">
              Nothing was recorded in this period.
            </p>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHead {...headProps("date", "Date")} />
                    <SortableHead {...headProps("users", "Users")} align="right" className="w-28" />
                    <SortableHead {...headProps("questions", "Questions")} align="right" className="w-32" />
                    <SortableHead
                      {...headProps("documents", "Documents referenced")}
                      align="right"
                      className="w-48"
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((d) => (
                    <TableRow key={d.date}>
                      <TableCell className="whitespace-nowrap text-xs tabular-nums">{dateFmt(d.date)}</TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums">{d.users}</TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums">{d.questions}</TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums">{d.documents}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type DocSortKey = "title" | "questions" | "pages" | "lastUsedAt";

function DocumentUsageTable({
  range,
  rows,
  loading,
}: {
  range: DateRange;
  rows: DocumentUsage[];
  loading: boolean;
}) {
  const { sort, dir, headProps } = useSort<DocSortKey>("questions", "desc", {
    questions: "desc",
    pages: "desc",
    lastUsedAt: "desc",
  });

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      switch (sort.key) {
        case "title":
          return a.title.localeCompare(b.title) * dir;
        case "pages":
          return (a.pages - b.pages) * dir;
        case "lastUsedAt":
          return (Date.parse(a.lastUsedAt) - Date.parse(b.lastUsedAt)) * dir;
        default:
          return (a.questions - b.questions) * dir;
      }
    });
  }, [rows, sort, dir]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Most-cited documents</CardTitle>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {dateFmt(range.start)} – {dateFmt(range.end)}
          </span>
          <Button
            size="sm"
            variant="secondary"
            className="text-xs"
            disabled={sorted.length === 0}
            onClick={() =>
              downloadCsv(
                datedFilename("cited-documents"),
                ["title", "questions", "pages", "last_cited", "document_id"],
                sorted.map((d) => [d.title, d.questions, d.pages, d.lastUsedAt.slice(0, 10), d.documentId]),
              )
            }
          >
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
        {!loading && sorted.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">
            No document was cited in an answer in this period.
          </p>
        )}
        {sorted.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead {...headProps("title", "Document")} />
                  <SortableHead {...headProps("questions", "Questions")} align="right" className="w-28" />
                  <SortableHead {...headProps("pages", "Pages")} align="right" className="w-24" />
                  <SortableHead {...headProps("lastUsedAt", "Last cited")} className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((d) => (
                  <TableRow key={d.documentId}>
                    <TableCell>
                      {/* A new tab, deliberately. Someone reading down this
                          table is comparing rows, and navigating away loses
                          both the sort and the date range they set. */}
                      <Link
                        to="/library/$documentId"
                        params={{ documentId: d.documentId }}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        {d.title} ↗
                      </Link>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums">{d.questions}</TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums">{d.pages}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                      {dateFmt(d.lastUsedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type UserSortKey = "email" | "questions" | "avgRating" | "lastQuestionAt";

function UserUsageTable({ rows, loading }: { rows: UserUsage[]; loading: boolean }) {
  const { sort, dir, headProps } = useSort<UserSortKey>("questions", "desc", {
    questions: "desc",
    avgRating: "desc",
    lastQuestionAt: "desc",
  });

  const sorted = useMemo(() => {
    // Nulls last in both directions: "never asked" and "unrated" are absences,
    // not low scores, and sorting them as zero would put them above people who
    // actually scored badly.
    const nulls = (a: number | null, b: number | null) => {
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      return null;
    };
    return [...rows].sort((a, b) => {
      switch (sort.key) {
        case "email":
          return a.email.localeCompare(b.email) * dir;
        case "avgRating": {
          const n = nulls(a.avgRating, b.avgRating);
          return n ?? ((a.avgRating as number) - (b.avgRating as number)) * dir;
        }
        case "lastQuestionAt": {
          const av = a.lastQuestionAt ? Date.parse(a.lastQuestionAt) : null;
          const bv = b.lastQuestionAt ? Date.parse(b.lastQuestionAt) : null;
          const n = nulls(av, bv);
          return n ?? ((av as number) - (bv as number)) * dir;
        }
        default:
          return (a.questions - b.questions) * dir;
      }
    });
  }, [rows, sort, dir]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>People</CardTitle>
        <div className="flex items-center gap-2">
          {/* ERIKA had a separate "inactive users" query whose SQL was
              byte-for-byte identical to its active-users one. It is one list;
              the zero rows at the bottom are the inactive people. */}
          <span className="text-xs text-muted-foreground">Everyone in the organisation</span>
          <Button
            size="sm"
            variant="secondary"
            className="text-xs"
            disabled={sorted.length === 0}
            onClick={() =>
              downloadCsv(
                datedFilename("people"),
                ["email", "questions", "avg_rating", "last_asked"],
                sorted.map((u) => [
                  u.email,
                  u.questions,
                  u.avgRating !== null ? u.avgRating.toFixed(2) : "",
                  u.lastQuestionAt?.slice(0, 10) ?? "",
                ]),
              )
            }
          >
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
        {sorted.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead {...headProps("email", "Person")} />
                  <SortableHead {...headProps("questions", "Questions")} align="right" className="w-28" />
                  <SortableHead {...headProps("avgRating", "Avg rating")} align="right" className="w-28" />
                  <SortableHead {...headProps("lastQuestionAt", "Last asked")} className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((u) => (
                  <TableRow key={u.userId} className={u.questions === 0 ? "opacity-60" : ""}>
                    <TableCell className="truncate" title={u.email}>
                      <a href={`mailto:${u.email}`} className="text-primary hover:underline">
                        {u.email}
                      </a>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums">{u.questions}</TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums">
                      {u.avgRating !== null ? u.avgRating.toFixed(1) : "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                      {u.lastQuestionAt ? dateFmt(u.lastQuestionAt) : "Never"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
